import { IconPencil } from "@tabler/icons-react";
import {
  type FC,
  type FocusEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { ButtonVariant } from "../Buttons/button.js";
import { IconButton } from "../Buttons/iconButton.js";
import { Size } from "../helpers/helpers.js";
import { Input } from "./input.js";

interface EditableTextProps {
  value: string;
  accessibleLabel: string;
  editAccessibleLabel?: string;
  onSave: (value: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  minLength?: number;
  maxLength?: number;
  isRequired?: boolean;
  isDisabled?: boolean;
}

/**
 * A single-line value that looks like plain text until activated. Clicking the
 * text or pencil begins editing; Enter or leaving the control saves, while
 * Escape cancels.
 */
export const EditableText: FC<EditableTextProps> = (props) => {
  const {
    value,
    accessibleLabel,
    editAccessibleLabel = `Edit ${accessibleLabel}`,
    onSave,
    className,
    inputClassName,
    minLength,
    maxLength,
    isRequired = false,
    isDisabled = false,
  } = props;
  const editorRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const shouldFocusEditButtonRef = useRef(false);
  const shouldFocusInputRef = useRef(false);
  const committedValueRef = useRef(value);
  const previousValuePropRef = useRef(value);
  const [draft, setDraft] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useLayoutEffect(() => {
    if (previousValuePropRef.current === value) {
      return;
    }

    const previousCommittedValue = committedValueRef.current;
    previousValuePropRef.current = value;
    committedValueRef.current = value;
    setDraft((currentDraft) =>
      !isEditing || currentDraft === previousCommittedValue
        ? value
        : currentDraft,
    );
  }, [isEditing, value]);

  useLayoutEffect(() => {
    if (isEditing && !isDisabled) {
      const input = inputRef.current;
      const cursorPosition = input?.value.length ?? 0;
      input?.focus();
      input?.setSelectionRange(cursorPosition, cursorPosition);
      if (input) {
        input.scrollLeft = input.scrollWidth;
      }
      return;
    }

    if (shouldFocusEditButtonRef.current) {
      shouldFocusEditButtonRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [isDisabled, isEditing]);

  useLayoutEffect(() => {
    if (isDisabled && isEditing) {
      setDraft(committedValueRef.current);
      setIsEditing(false);
    }
  }, [isDisabled, isEditing]);

  useLayoutEffect(() => {
    if (isSaving || !shouldFocusInputRef.current) {
      return;
    }

    shouldFocusInputRef.current = false;
    if (isEditing && !isDisabled) {
      inputRef.current?.focus();
    }
  }, [isDisabled, isEditing, isSaving]);

  const finishEditing = (shouldRestoreFocus: boolean) => {
    const activeElement = document.activeElement;
    const restoreFocus =
      shouldRestoreFocus &&
      (activeElement === document.body ||
        editorRef.current?.contains(activeElement) === true);

    shouldFocusEditButtonRef.current = restoreFocus;
    setIsEditing(false);
  };

  const cancel = (shouldRestoreFocus = true) => {
    setDraft(committedValueRef.current);
    finishEditing(shouldRestoreFocus);
  };

  const save = async (shouldRestoreFocus = true) => {
    const nextValue = draft.trim();
    const minimumLength = Math.max(isRequired ? 1 : 0, minLength ?? 0);
    const committedValueAtStart = committedValueRef.current;

    if (isSaving) {
      return;
    }

    if (nextValue.length < minimumLength) {
      cancel(shouldRestoreFocus);
      return;
    }

    if (nextValue === committedValueAtStart.trim()) {
      setDraft(committedValueAtStart);
      finishEditing(shouldRestoreFocus);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(nextValue);
      const latestCommittedValue = committedValueRef.current;
      if (
        latestCommittedValue !== committedValueAtStart &&
        latestCommittedValue.trim() !== nextValue
      ) {
        setDraft(latestCommittedValue);
      } else {
        committedValueRef.current = nextValue;
        setDraft(nextValue);
      }
      finishEditing(shouldRestoreFocus);
    } catch {
      shouldFocusInputRef.current = true;
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLSpanElement>) => {
    if (!isEditing || event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    void save(false);
  };

  const startEditing = () => {
    if (isDisabled) {
      return;
    }

    setDraft(committedValueRef.current);
    setIsEditing(true);
  };

  return (
    <span
      ref={editorRef}
      className={twMerge("flex w-full min-w-0 items-center gap-1", className)}
      onBlur={handleBlur}
    >
      <Input
        ref={inputRef}
        accessibleLabel={accessibleLabel}
        inputClassName={twMerge(
          "px-2",
          isEditing
            ? "w-full"
            : "[field-sizing:content] w-auto max-w-full pr-0",
          !isEditing &&
            "truncate bg-transparent shadow-none ring-0 focus:ring-0",
          !isEditing && (isDisabled ? "cursor-default" : "cursor-text"),
          inputClassName,
        )}
        isDisabled={isSaving}
        isInline
        minLength={minLength}
        maxLength={maxLength}
        onChange={(event) => setDraft(event.target.value)}
        onClick={isEditing || isDisabled ? undefined : startEditing}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void save();
          }

          if (event.key === "Escape") {
            event.preventDefault();
            cancel();
          }
        }}
        readOnly={!isEditing || isDisabled}
        size={Size.SMALL}
        tabIndex={isEditing ? undefined : -1}
        value={draft}
        className={twMerge(
          "min-w-0",
          isEditing ? "flex-1" : "max-w-[calc(100%-2rem)] shrink",
        )}
      />

      {!isEditing && !isDisabled ? (
        <IconButton
          ref={editButtonRef}
          accessibleLabel={editAccessibleLabel}
          className="text-muted-foreground"
          icon={<IconPencil aria-hidden="true" className="size-4" />}
          onClick={startEditing}
          size={Size.SMALL}
          variant={ButtonVariant.Tertiary}
        />
      ) : null}
    </span>
  );
};
